/**
 * The submit-refusal classifier (SPEC-SOR-424 R13/R16).
 *
 * A submit refused by the server lands on the wizard as a thrown error, and the
 * two refusals it can be call for opposite handling: a QUOTE refusal means the
 * order's quote is missing, stale, expired or degraded, so the figures the
 * filer was shown are not the figures the server would honor and the wizard's
 * fee record must be cleared to the absent state (which structurally re-blocks
 * submit); a PAYMENT refusal means the quote is current and the order simply
 * carries no card, so the record must survive exactly as it is and the filer is
 * sent to add one. Telling them apart is this module's whole job.
 *
 * The two classes are the api-gateway's, decided there by `quoteRefusal`
 * (`services/server-quote-verdict.ts`) and `paymentRefusal`
 * (`services/server-payment-rule.ts`). Their wire vocabulary is a closed
 * contract, so it has exactly one home: `QUOTE_REFUSAL_CODES` /
 * `PAYMENT_REFUSAL_CODES` in the shared package (`order-logic/common/
 * server-fee-quote.ts`, beside the `QUOTE_VERDICTS` value space they are the
 * refusable subset of), read here rather than re-declared — a code added or
 * renamed on the server is matched by this classifier the moment it exists. The
 * `X-Reauth-Required` precedent for apps keeping their own copy covers a fetch
 * header with no shared contract package in play; it does not reach this.
 *
 * The two classes stay DISJOINT by construction: the status AND the code both
 * decide, 409 with a `quote_*` code against 402 with `payment_method_required`,
 * so no submit can be claimed by both (SPEC-SOR-424 AX23, mirrored from the
 * server side).
 *
 * The classifier reads the refusal facts off the thrown value STRUCTURALLY
 * (`status` / `code`), never through `instanceof ApiError`. The decision is
 * about the wire facts, which is what an `ApiError` carries; narrowing on the
 * JS class would make the verdict depend on which Error subclass happened to
 * carry them, and a server-side code added later classifies `null` — the
 * generic failure path, unchanged.
 *
 * Kept dependency-light and React-free (mirroring `submit-outcome-routing.ts`)
 * so the spec property probes under internal/proptests compile it directly and
 * every call stays synchronous.
 */

import { PAYMENT_REFUSAL_CODES, QUOTE_REFUSAL_CODES } from '@lds-efsp/shared-contracts';

/**
 * Which refusal class a failed submit belongs to:
 *   `requote` — the quote refusal (409). Clear the fee record; the server's own
 *               message carries the re-quote instruction.
 *   `payment` — the payment refusal (402). Leave the record resolved and drop
 *               the client's local card, so the add-a-card checklist demands
 *               the one the server says the order is missing.
 *   `null`    — not a refusal: every other failure keeps the generic path.
 */
export type SubmitRefusalClass = 'requote' | 'payment';

/** The refusal facts as they ride the thrown value. `null` = the field isn't there. */
interface RefusalFacts {
  status: number | null;
  code: string | null;
}

/**
 * Read the wire facts off a thrown value without asserting anything about its
 * class or shape beyond the two fields being present with the right types.
 */
function refusalFacts(err: unknown): RefusalFacts {
  if (typeof err !== 'object' || err === null) return { status: null, code: null };
  const candidate = err as { status?: unknown; code?: unknown };
  return {
    status: typeof candidate.status === 'number' ? candidate.status : null,
    code: typeof candidate.code === 'string' && candidate.code ? candidate.code : null,
  };
}

/**
 * Classify a failed submit attempt onto one of the two refusal classes, or
 * `null` when it is neither. Total over `unknown`: a network `TypeError`, a
 * 500, a 403 and a body with no code all answer `null`, so a refusal the portal
 * does not know degrades to the generic error path rather than being guessed
 * into one.
 */
export function classifySubmitRefusal(err: unknown): SubmitRefusalClass | null {
  const { status, code } = refusalFacts(err);
  if (code === null) return null;
  if (status === 409 && (QUOTE_REFUSAL_CODES as readonly string[]).includes(code)) return 'requote';
  if (status === 402 && (PAYMENT_REFUSAL_CODES as readonly string[]).includes(code)) return 'payment';
  return null;
}
